import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export function useOutlookStatus() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/outlook/status", { params: { origin: window.location.origin } }).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return [st, load];
}

export async function connectOutlook() {
  try {
    const { data } = await api.get("/outlook/auth-url", { params: { origin: window.location.origin } });
    window.location.href = data.url;
  } catch (e) { toast.error(errMsg(e)); }
}

export function OutlookBanner() {
  const [st] = useOutlookStatus();
  if (!st || st.connected) return null;
  return (
    <div className="kh-card flex flex-wrap items-center gap-4 border-primary/30 bg-primary/5 p-4" data-testid="outlook-banner">
      <Mail className="h-5 w-5 text-primary" strokeWidth={1.75} />
      <div className="flex-1 text-sm"><span className="font-medium">Outlook isn't connected yet.</span> <span className="text-muted-foreground">Sign in once so approved drafts land in your mailbox and replies sync back.</span></div>
      <Button onClick={connectOutlook} disabled={!st.configured} className="rounded-full" data-testid="outlook-banner-connect-btn">Sign in to Outlook</Button>
      <Link to="/outlook" className="text-xs text-primary hover:underline" data-testid="outlook-banner-details-link">Details</Link>
    </div>
  );
}

export default function OutlookConnect() {
  const [st, load] = useOutlookStatus();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("outlook") === "connected") toast.success("Outlook connected");
    if (params.get("outlook") === "error") toast.error(`Outlook: ${params.get("msg") || "connection failed"}`);
    if (params.get("outlook")) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const disconnect = async () => { await api.post("/outlook/disconnect"); toast.success("Outlook disconnected"); load(); };
  if (!st) return null;
  return (
    <div className="kh-card space-y-6 p-6 sm:p-8" data-testid="outlook-card">
      <div className="flex items-start gap-4">
        <div className={`grid h-12 w-12 place-items-center rounded-full ${st.connected ? "bg-emerald-600/10" : "bg-primary/10"}`}>
          {st.connected ? <CheckCircle2 className="h-6 w-6 text-emerald-700" /> : <Mail className="h-6 w-6 text-primary" />}
        </div>
        <div className="flex-1">
          <h3 className="font-display text-2xl">{st.connected ? "Outlook is connected" : "Sign in to Outlook"}</h3>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="outlook-connection-status">
            {st.connected ? `Mailbox ${st.mailbox} · connected ${fmtDateTime(st.connected_at)}` : st.configured ? "Use your Microsoft 365 / Outlook account. You'll be sent to Microsoft's secure sign-in page and brought straight back here." : "Microsoft app credentials are not configured on the server."}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {!st.connected && <Button size="lg" className="rounded-full px-8" onClick={connectOutlook} disabled={!st.configured} data-testid="outlook-connect-btn">Sign in with Microsoft</Button>}
        {st.connected && <Button variant="outline" onClick={connectOutlook} data-testid="outlook-reconnect-btn">Switch account</Button>}
        {st.connected && <Button variant="ghost" onClick={disconnect} data-testid="outlook-disconnect-btn">Disconnect</Button>}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {[["Drafts only", "The portal creates drafts in your mailbox. It never sends — you review and send in Outlook."],
          ["Reply tracking", "Sync checks Sent Items and Inbox for each thread to mark sent emails and replies."],
          ["Private", "Your sign-in token is encrypted on the server. Disconnect any time."]].map(([t, d]) => (
          <div key={t} className="rounded-lg border bg-background/60 p-4">
            <div className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4 text-primary" />{t}</div>
            <p className="mt-1 text-xs text-muted-foreground">{d}</p>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="outlook-azure-setup">
        <div className="font-medium">Seeing "AADSTS500113: No reply address is registered"?</div>
        <p className="mt-1 text-xs text-muted-foreground">Add this exact address in Azure Portal → App registrations → your app → <b>Authentication</b> → <b>Add a platform → Web</b> → Redirect URIs, then Save and try again:</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="rounded bg-card px-2 py-1 font-mono text-xs" data-testid="outlook-redirect-uri">{st.redirect_uri}</code>
          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(st.redirect_uri); toast.success("Redirect URI copied"); }} data-testid="outlook-copy-redirect-btn">Copy</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Delegated API permissions (Microsoft Graph): Mail.ReadWrite, User.Read, offline_access, openid, profile, email.</p>
      </div>
    </div>
  );
}
